import React, { useState, useEffect } from 'react'
import { Box, Typography, Chip, Tabs, Tab, GlobalStyles } from '@mui/material'
import ScienceIcon from '@mui/icons-material/Science'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import EditIcon from '@mui/icons-material/Edit'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import { useAppStore } from '@/store'
import { clearCurrentUser } from '@/db/indexedDB'
import { CreateUser } from './CreateUser'
import { UpdateUsers } from './UpdateUsers'
import { MyTestUsers } from './MyTestUsers'
import { MastercardProfiles } from './MastercardProfiles'

export const Developer: React.FC = () => {
  const env = import.meta.env.MODE
  const { setUser } = useAppStore()
  const [activeTab, setActiveTab] = useState(0)

  // Clear any selected customer so the breadcrumb and SubHeader tabs don't bleed into this view.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setUser(null)
    clearCurrentUser()
  }, []) // intentionally empty — run once on mount

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: { xs: '100%', md: 1100, lg: 1300, xl: 1500 }, mx: 'auto' }}>
      {/* Scoped to this page only (unmounts on navigation) — reserves the scrollbar's width so the
          tab bar and content don't shift horizontally when a tab's content height toggles the scrollbar */}
      <GlobalStyles styles={{ html: { scrollbarGutter: 'stable' } }} />

      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <ScienceIcon sx={{ color: '#fa624b', fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700} color="#2c063f">Developer Tools</Typography>
        <Chip
          label={env.toUpperCase()}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: 11,
            backgroundColor: env === 'uat' ? '#ffaa00' : '#2c063f',
            color: env === 'uat' ? '#2c063f' : '#fff',
          }}
        />
      </Box>
      <Typography variant="body2" color="#a08fa8" mb={3}>
        Create and manage test users in the{' '}
        <strong style={{ color: '#2c063f' }}>{env}</strong> environment.
        These users are not real customers.
      </Typography>

      {/* Sub-navigation — fixed-width tabs so the indicator/layout doesn't jump between labels of different lengths */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          mb: 3,
          borderBottom: '1px solid #e0d7e4',
          '& .MuiTab-root': { color: '#a08fa8', fontWeight: 500, textTransform: 'none', minWidth: 180, px: 1.5 },
          '& .Mui-selected': { color: '#2c063f !important', fontWeight: 700 },
          '& .MuiTabs-indicator': { backgroundColor: '#fa624b', height: 3 },
        }}
      >
        <Tab icon={<PersonAddIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Create user" />
        <Tab icon={<EditIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Update users" />
        <Tab icon={<PersonSearchIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="My test users" />
        <Tab icon={<CreditCardIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Mastercard profiles" />
      </Tabs>

      {/* Tab content */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <CreateUser />
        </Box>
      )}
      {activeTab === 1 && <UpdateUsers />}
      {activeTab === 2 && <MyTestUsers />}
      {activeTab === 3 && <MastercardProfiles />}

    </Box>
  )
}
